import { apiFetch } from "./apiClient";

/**
 * Aviso persistente de un usuario (round 2026-10-28). Hoy solo existe el tipo `aseos_ventana`:
 * «N aseos nuevos disponibles para tomar en <sector>», para las aseadoras. NO es una alerta
 * administrativa (`alerts.api.ts`): esto es de cada persona y solo ella lo ve.
 */
export interface Notificacion {
  id: number;
  tipo: string;
  titulo: string;
  mensaje: string;
  /** Para `aseos_ventana`: { sector, sectorId, cantidad, fecha, aseoIds, ruta }. */
  datos: {
    ruta?: string;
    sector?: string;
    cantidad?: number;
    [k: string]: unknown;
  };
  leida: boolean;
  created_at: string;
  actualizada_en: string;
}

export interface NotificacionesLista {
  data: Notificacion[];
  noLeidas: number;
}

/**
 * Cliente de /api/notificaciones. Todas las llamadas son silenciosas (`silent`): el contador se
 * consulta cada minuto de fondo y un fallo de red no debe llenar la pantalla de avisos de error.
 *
 * Ante un error (`apiFetch` devuelve `null`), `listar` y `contador` devuelven `null` —NO una lista vacía ni
 * un 0—: «no se pudo consultar» y «no tienes avisos» son cosas distintas. Antes se convertían en
 * `{ data: [], noLeidas: 0 }` / `0`, y una caída momentánea de la red apagaba el número de la campana y el
 * panel decía «No tienes avisos» aunque hubiera varios sin leer (round 2026-10-28). Quien llama conserva
 * lo que ya mostraba.
 */
export const notificacionesApi = {
  /** GET /notificaciones — las propias, más recientes primero, con el contador de no leídas (null si falló). */
  listar: async (limit = 30): Promise<NotificacionesLista | null> => {
    const res = await apiFetch<{ data?: Notificacion[]; noLeidas?: number }>(
      `/notificaciones?limit=${limit}`,
      { silent: true },
    );
    if (!res) return null;
    return {
      data: Array.isArray(res.data) ? res.data : [],
      noLeidas: typeof res.noLeidas === "number" ? res.noLeidas : 0,
    };
  },

  /** GET /notificaciones/contador — cuántas tiene sin leer (null si falló). */
  contador: async (): Promise<number | null> => {
    const res = await apiFetch<{ noLeidas?: number }>("/notificaciones/contador", {
      silent: true,
    });
    if (!res) return null;
    return typeof res.noLeidas === "number" ? res.noLeidas : 0;
  },

  /** PATCH /notificaciones/:id/leida — devuelve las que quedan sin leer (null si falló). */
  marcarLeida: async (id: number): Promise<number | null> => {
    const res = await apiFetch<{ noLeidas?: number }>(`/notificaciones/${id}/leida`, {
      method: "PATCH",
      silent: true,
    });
    return res && typeof res.noLeidas === "number" ? res.noLeidas : null;
  },

  /** POST /notificaciones/leer-todas */
  marcarTodasLeidas: async (): Promise<boolean> => {
    const res = await apiFetch<{ success?: boolean }>("/notificaciones/leer-todas", {
      method: "POST",
      silent: true,
    });
    return Boolean(res?.success);
  },
};
